import { Button } from '@ag1/ui';
import {
  useScrollPaddingTopHandler,
  type ScrollPaddingTopHeaderType
} from '@portal/atoms/scrollPaddingTopAtom';
import { bannerClickEvent } from 'analytics/events/banners';
import clsx from 'clsx';
import Image from 'next/image';
import React, { FC, useMemo, useRef } from 'react';
import useAddAccessibility from 'shared/hooks/addAccessibility';
import { shouldShowForCountry } from 'shared/utils/shouldShowForCountry';
import { urlFor } from 'shared/utils/urlFor';
import { useCloudfrontLocationQuery } from 'state/hooks/useCloudfrontLocation';
import { HeaderFullV2 } from 'types/studio/studio';
import styles from './Banner.module.scss';

export enum BannerType {
  OMEGA_OFFER_BAB = 'Omega Offer BaB',
  OMEGA_OFFER_SUB = 'Omega Offer Sub & Save',
  AG1_HAT_D3 = 'AG1 Hat + D3',
  OMEGA_D3 = 'Omega + D3',
  HUBERMAN = 'Huberman',
  DAN_CHURCHILL_POP_UP = 'Dan Churchill Pop-Up'
}

export interface BannerProps {
  banner: HeaderFullV2['banner'] & {
    buttonText?: string;
    countries: string[];
  };
  bannerType: BannerType;
  link?: string;
  className?: string;
  scrollPaddingHeaderType?: ScrollPaddingTopHeaderType;
  asStatic?: boolean;
  skipButtonHeight?: number;
}

const Banner: FC<BannerProps> = ({
  banner,
  bannerType,
  link,
  className,
  scrollPaddingHeaderType = 'pre-header',
  asStatic,
  skipButtonHeight = 0
}) => {
  const { data: cloudfrontLocation } = useCloudfrontLocationQuery();
  const SectionNode = link ? 'a' : 'div';
  const sectionProps = link ? { href: link } : {};
  const bannerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useAddAccessibility(contentRef, true);
  const shouldShow = shouldShowForCountry(banner, cloudfrontLocation?.country);
  const { height: bannerHeight, top: bannerTop } = useScrollPaddingTopHandler(
    {
      type: scrollPaddingHeaderType,
      disable: !shouldShow
    },
    bannerRef
  );

  const rootStyles = useMemo<React.CSSProperties>(() => {
    if (asStatic) {
      return {
        position: `relative`,
        top: 0,
        paddingTop: 0
      };
    }
    return { paddingTop: `${bannerHeight}px` };
  }, [asStatic, bannerHeight]);

  if (!shouldShow) {
    return null;
  }

  const handleBanner = () => {
    if (link) {
      const clickDate = new Date().toISOString();
      bannerClickEvent({
        bannerType,
        bannerHeadline: banner.headline,
        bannerLink: link,
        clickDate
      });
      window.location.href = link;
    }
  };

  return (
    <section className={className} style={rootStyles}>
      <SectionNode
        {...sectionProps}
        ref={bannerRef as any}
        className={clsx(styles.banner, asStatic && styles.headerGroupItem)}
        style={
          asStatic
            ? undefined
            : ({
                '--skip-button-height': `${skipButtonHeight}px`,
                top: bannerTop
              } as React.CSSProperties)
        }>
        {banner?.image && (
          <Image
            src={banner?.image ? urlFor(banner?.image).url : ''}
            alt={banner?.image?.alt || ''}
            width={72}
            height={72}
            className={styles.image}
            priority
          />
        )}
        <p className={styles.body}>{banner?.body}</p>
        <h3 className={styles.headline} ref={contentRef}>
          {banner.notice && (
            <span
              className={`${styles.notice} ${
                styles[banner.noticeColor ? banner.noticeColor : '']
              }`}>
              {`${banner.notice} `}
            </span>
          )}
          {banner?.headline}
        </h3>
        {banner?.buttonText && (
          <Button
            variant="primary"
            theme="new"
            size="xSmall"
            rounded
            onClick={handleBanner}
            className={styles.button}>
            {banner?.buttonText}
          </Button>
        )}
      </SectionNode>
    </section>
  );
};

export { Banner };
